/*
 * @Author: o.xiewanqing
 * @Date: 2026-01-21 17:10:05
 * @LastEditors: o.xiewanqing
 * @LastEditTime: 2026-01-22 11:43:42
 * @Description: 导出记录徽标 Hook，用于管理导出操作的红点数字徽标
 */
import { useState, useCallback, useEffect } from 'react';

const EXPIRATION_TIME = 24 * 60 * 60 * 1000; // 24小时

/**
 * 获取未过期的时间戳数组
 */
const getValidTimestamps = (storageKey: string): number[] => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (!stored) {
      return [];
    }
    const timestamps: number[] = JSON.parse(stored);
    const now = Date.now();
    // 过滤掉已过期的时间戳
    const validTimestamps = timestamps.filter((timestamp) => timestamp > now);
    // 如果有过期的，更新 localStorage
    if (validTimestamps.length !== timestamps.length) {
      localStorage.setItem(storageKey, JSON.stringify(validTimestamps));
    }
    return validTimestamps;
  } catch (error) {
    console.error('获取导出记录时间戳失败:', error);
    return [];
  }
};

/**
 * 添加新的导出记录时间戳
 */
const addExportTimestamp = (storageKey: string): void => {
  try {
    const timestamps = getValidTimestamps(storageKey);
    const expirationTime = Date.now() + EXPIRATION_TIME;
    timestamps.push(expirationTime);
    localStorage.setItem(storageKey, JSON.stringify(timestamps));
  } catch (error) {
    console.error('添加导出记录时间戳失败:', error);
  }
};

/**
 * 清空所有导出记录时间戳
 */
const clearExportTimestamps = (storageKey: string): void => {
  try {
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.error('清空导出记录时间戳失败:', error);
  }
};

/**
 * 获取徽标数字
 */
const getBadgeCount = (storageKey: string): number => {
  const timestamps = getValidTimestamps(storageKey);
  return timestamps.length;
};

/**
 * 清空所有以 hll_export_badge_ 开头的导出记录缓存
 */
export const clearAllExportBadgeCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    // 遍历所有 localStorage 的 key
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('hll_export_badge_')) {
        keysToRemove.push(key);
      }
    }
    // 删除所有匹配的 key
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('清空导出记录缓存失败:', error);
  }
};

type UseExportBadgeOptions = {
  storageKey: string; // localStorage 存储 key
};

export const useExportBadge = (options: UseExportBadgeOptions) => {
  const { storageKey } = options;

  const [badgeCount, setBadgeCount] = useState<number>(() =>
    getBadgeCount(storageKey)
  );

  // 当 storageKey 变化时，更新 badgeCount
  useEffect(() => {
    const newCount = getBadgeCount(storageKey);
    setBadgeCount(newCount);
  }, [storageKey]);

  // 添加导出记录时间戳
  const handleBadgeAdd = useCallback(() => {
    addExportTimestamp(storageKey);
    const newCount = getBadgeCount(storageKey);
    setBadgeCount(newCount);
  }, [storageKey]);

  // 清空导出记录时间戳
  const handleBadgeClear = useCallback(() => {
    clearExportTimestamps(storageKey);
    const newCount = getBadgeCount(storageKey);
    setBadgeCount(newCount);
  }, [storageKey]);

  return {
    badgeCount,
    handleBadgeAdd,
    handleBadgeClear,
  };
};
